import {
  AnalyticsEvents,
  EmailLoginButtonClickedProperties,
  LoginCompletedMethod,
  LoginCompletedProperties,
  LoginFailedMethod,
  LoginFailedProperties,
  LoginPageViewedProperties,
  LoginStartedMethod,
  LoginStartedProperties,
} from '@mondaygpt/analytics-events'
import { TokenResponse } from '@react-oauth/google'
import { useAnalytics } from 'hooks'
import { getDeviceId, useAppDispatch, useAppSelector } from 'modules/core'
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ProviderSigninRequestDto } from 'openapi'
import { clearOtpState, providerSigninAction, requestOtpAction, selectOtpState } from '../slices'
import { BaseLoginFormView } from './BaseLoginFormView'
import { BaseLoginPageLayout } from './BaseLoginPageLayout'
import { OtpVerificationForm } from './OtpVerificationForm'

type FormStep = 'email' | 'otp'

const useEmailFromQuery = () => {
  const { search } = useLocation()
  return useMemo(() => {
    const searchParams = new URLSearchParams(search)
    const email = searchParams.get('email')
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isValid = email ? emailRegex.test(email) : false
    return { email: isValid ? email : null, isValid }
  }, [search])
}

const useRedirectParams = () => {
  const { search } = useLocation()
  return useMemo(() => {
    const searchParams = new URLSearchParams(search)
    const rawRedirect = searchParams.get('redirectUrl')
    // Open-redirect guard: resolve against our origin and require it to stay
    // there. The URL parser normalizes backslashes and strips tab/newline
    // BEFORE the origin check, so '/\evil.com' and '/%09/evil.com'-style
    // bypasses (which a raw startsWith('//') test misses) are rejected too.
    let redirectTo: string | null = null
    if (rawRedirect?.startsWith('/')) {
      try {
        const resolved = new URL(rawRedirect, window.location.origin)
        if (resolved.origin === window.location.origin) {
          redirectTo = resolved.pathname + resolved.search + resolved.hash
        }
      } catch {
        // Unparseable input → treat as no redirect.
      }
    }
    const prompt = searchParams.get('prompt')
    return { redirectTo, prompt }
  }, [search])
}

const useAutoOtpRequest = (email: string | null, isValid: boolean) => {
  const dispatch = useAppDispatch()
  const { track } = useAnalytics()

  useEffect(() => {
    if (isValid && email) {
      // Track login from landing page
      track(AnalyticsEvents.Authentication.LOGIN_FROM_LANDING_PAGE)

      dispatch(requestOtpAction(email))
    }
  }, [dispatch, email, isValid, track])
}

export const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const { isRequesting, otpSent, error } = useAppSelector(selectOtpState)

  const { email: queryEmail, isValid: isQueryEmailValid } = useEmailFromQuery()
  const { redirectTo, prompt } = useRedirectParams()

  const [email, setEmail] = useState(queryEmail || '')
  const [validEmail, setValidEmail] = useState(isQueryEmailValid)
  const [currentStep, setCurrentStep] = useState<FormStep>(isQueryEmailValid ? 'otp' : 'email')

  useAutoOtpRequest(queryEmail, isQueryEmailValid)

  // Handle post-login redirect with prompt support.
  // `result` is the verify/signin response; we use it to route a freshly
  // signed-in invitee (no business of their own + a pending invite) to the
  // full invitation screen instead of the business-creation wizard.
  const handlePostLoginRedirect = (result?: {
    requiresBusinessCreation?: boolean
    hasPendingInvite?: boolean
  }) => {
    if (redirectTo) {
      // An explicit redirect (e.g. from the emailed invite link) always wins.
      const redirectPath = prompt
        ? `${redirectTo}?prompt=${encodeURIComponent(prompt)}`
        : redirectTo
      navigate(redirectPath)
      return
    }
    if (result?.hasPendingInvite) {
      // An invited user — show them the invitation first, whether they're brand
      // new (would otherwise land in onboarding/paywall) or already running
      // their own business (nothing else surfaces a pending invite in-app, the
      // other half of the July 2026 stranding: a stray self-created business
      // made the invite invisible forever). The accept screen offers dismissal
      // and "create my own business instead", so this never traps anyone.
      navigate('/accept-invite')
    }
    // Otherwise fall through: the OnboardingGuard routes to /onboarding or Home.
  }

  // Track page view when component mounts. localStorage is wrapped because
  // private-mode Safari throws on access (storage quota = 0); a throw here
  // would crash the whole login page.
  useEffect(() => {
    let isFirstVisit = true
    try {
      isFirstVisit = !localStorage.getItem('login_page_visited')
    } catch {
      // Storage unavailable — treat as first visit, skip the marker write.
    }

    track(AnalyticsEvents.Authentication.LOGIN_PAGE_VIEWED, {
      referrer: document.referrer || undefined,
      is_first_visit: isFirstVisit,
    } satisfies LoginPageViewedProperties)

    try {
      localStorage.setItem('login_page_visited', 'true')
    } catch {
      // Same as above — silently skip when storage is unavailable.
    }
  }, [track])

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    setValidEmail(emailRegex.test(value))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validEmail) return

    // Track button click for form submission
    track(AnalyticsEvents.Authentication.EMAIL_LOGIN_BUTTON_CLICKED, {
      email: email,
    } satisfies EmailLoginButtonClickedProperties)

    // Track login started event
    track(AnalyticsEvents.Authentication.LOGIN_STARTED, {
      method: LoginStartedMethod.EMAIL,
      referrer: document.referrer || window.location.pathname,
    } satisfies LoginStartedProperties)

    try {
      await dispatch(requestOtpAction(email)).unwrap()
      setCurrentStep('otp')
    } catch (err) {
      // Track login failed event for OTP request failure
      track(AnalyticsEvents.Authentication.LOGIN_FAILED, {
        method: LoginFailedMethod.EMAIL,
        error_message: err instanceof Error ? err.message : 'Failed to send OTP',
        error_code: 'otp_request_failed',
      } satisfies LoginFailedProperties)

      // Error is handled in the reducer
      console.error('OTP request failed:', err)
    }
  }

  const onGoogleClick = () => {
    track(AnalyticsEvents.Authentication.LOGIN_STARTED, {
      method: LoginStartedMethod.GOOGLE,
      referrer: document.referrer || window.location.pathname,
    })
  }

  const onGoogleLogin = async (tokenResponse: TokenResponse) => {
    try {
      const deviceId = getDeviceId()
      const dto: ProviderSigninRequestDto = { token: tokenResponse.access_token, deviceId }
      const result = await dispatch(providerSigninAction({ provider: 'google', dto })).unwrap()

      track(AnalyticsEvents.Authentication.LOGIN_COMPLETED, {
        method: LoginCompletedMethod.GOOGLE,
        user_id: result.user.id,
      } satisfies LoginCompletedProperties)

      // Handle redirect after successful login
      handlePostLoginRedirect(result)
    } catch (err) {
      // Track login failed event for OTP verification failure
      track(AnalyticsEvents.Authentication.LOGIN_FAILED, {
        method: LoginFailedMethod.GOOGLE,
        error_message: err instanceof Error ? err.message : 'Google login failed',
        error_code: 'google_login_failed',
      } satisfies LoginFailedProperties)

      console.error('Google login failed:', err)
    }
  }

  const onGoogleError = () => {
    track(AnalyticsEvents.Authentication.LOGIN_FAILED, {
      method: LoginFailedMethod.GOOGLE,
    } satisfies LoginFailedProperties)
  }
  const handleBack = () => {
    dispatch(clearOtpState())
    setCurrentStep('email')
  }

  // Show OTP form if we're on the OTP step or if OTP was sent
  const showOtpForm = currentStep === 'otp' || otpSent

  // Render login page using unified config-driven components
  return (
    <BaseLoginPageLayout>
      {showOtpForm ? (
        <OtpVerificationForm onBack={handleBack} onSuccess={handlePostLoginRedirect} />
      ) : (
        <BaseLoginFormView
          email={email}
          validEmail={validEmail}
          isRequesting={isRequesting}
          error={error}
          onEmailChange={handleEmailChange}
          onSubmit={handleSubmit}
          onGoogleClick={onGoogleClick}
          onGoogleLogin={onGoogleLogin}
          onGoogleError={onGoogleError}
        />
      )}
    </BaseLoginPageLayout>
  )
}
